import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/utils/api';
import { Plus, CheckCircle, DollarSign, AlertTriangle, CreditCard } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Layout from '@/components/Layout';
import ModuleIntelligenceBlock from '@/components/ModuleIntelligenceBlock';

const statusColors = {
  paid: 'bg-green-100 text-green-800',
  unpaid: 'bg-yellow-100 text-yellow-800',
  overdue: 'bg-red-100 text-red-800',
  partial: 'bg-blue-100 text-blue-800'
};

const PIE_COLORS = ['#22c55e', '#eab308', '#ef4444', '#3b82f6'];

export default function Payments() {
  const { t } = useTranslation();
  const [payments, setPayments] = useState([]);
  const [leases, setLeases] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [payForm, setPayForm] = useState({ paid_amount: '', payment_method: 'bank_transfer' });
  const [formData, setFormData] = useState({
    lease_id: '', tenant_id: '', amount: '', due_date: '', payment_type: 'rent'
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, leasesRes, tenantsRes] = await Promise.all([
        api.getPayments(), api.getLeases(), api.getTenants()
      ]);
      setPayments(paymentsRes.data);
      setLeases(leasesRes.data);
      setTenants(tenantsRes.data);
    } catch (error) { console.error('Failed:', error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const lease = leases.find(l => l.id === formData.lease_id);
    try {
      await api.createPayment({
        ...formData,
        tenant_id: lease?.tenant_id || formData.tenant_id,
        amount: parseFloat(formData.amount),
        due_date: new Date(formData.due_date).toISOString()
      });
      setDialogOpen(false);
      setFormData({ lease_id: '', tenant_id: '', amount: '', due_date: '', payment_type: 'rent' });
      fetchData();
    } catch (error) { console.error('Failed:', error); }
  };

  const handleMarkPaid = async () => {
    if (!selectedPayment) return;
    try {
      await api.markPaymentPaid(selectedPayment.id, parseFloat(payForm.paid_amount), payForm.payment_method);
      setPayDialogOpen(false);
      setSelectedPayment(null);
      fetchData();
    } catch (error) { console.error('Failed:', error); }
  };

  const openPayDialog = (payment) => {
    setSelectedPayment(payment);
    setPayForm({ paid_amount: payment.amount.toString(), payment_method: 'bank_transfer' });
    setPayDialogOpen(true);
  };

  const handleStripePay = async (payment) => {
    try {
      const res = await api.createStripeCheckout(payment.id);
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error('Stripe checkout failed:', error);
    }
  };

  const getTenantName = (id) => tenants.find(t => t.id === id)?.full_name || '-';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';

  const filteredPayments = filterStatus === 'all' ? payments : payments.filter(p => p.status === filterStatus);

  const totalPaid = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);
  const totalUnpaid = payments.filter(p => p.status === 'unpaid').reduce((sum, p) => sum + p.amount, 0);
  const totalOverdue = payments.filter(p => p.status === 'overdue').reduce((sum, p) => sum + p.amount, 0);

  const pieData = [
    { name: t('paid'), value: payments.filter(p => p.status === 'paid').length },
    { name: t('unpaid'), value: payments.filter(p => p.status === 'unpaid').length },
    { name: t('overdue'), value: payments.filter(p => p.status === 'overdue').length },
    { name: t('partial'), value: payments.filter(p => p.status === 'partial').length }
  ].filter(d => d.value > 0);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">{t('payments')}</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button><Plus className="mr-2 h-4 w-4" /> Add Payment</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Payment Record</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>{t('leases')}*</Label>
                  <Select value={formData.lease_id} onValueChange={(v) => {
                    const lease = leases.find(l => l.id === v);
                    setFormData({ ...formData, lease_id: v, tenant_id: lease?.tenant_id || '', amount: lease?.monthly_rent?.toString() || '' });
                  }}>
                    <SelectTrigger><SelectValue placeholder="Select lease" /></SelectTrigger>
                    <SelectContent>
                      {leases.filter(l => l.status === 'active').map(l => (
                        <SelectItem key={l.id} value={l.id}>{getTenantName(l.tenant_id)} - {l.monthly_rent} SAR</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('amount')} (SAR)*</Label>
                  <Input type="number" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required />
                </div>
                <div>
                  <Label>{t('dueDate')}*</Label>
                  <Input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} required />
                </div>
                <Button type="submit" className="w-full">{t('save')}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Financial Summary */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <DollarSign className="h-5 w-5 text-green-600 mb-2" />
              <div className="text-2xl font-bold text-green-600">{totalPaid.toLocaleString()} SAR</div>
              <div className="text-sm text-gray-500">{t('paid')}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mb-2" />
              <div className="text-2xl font-bold text-yellow-600">{totalUnpaid.toLocaleString()} SAR</div>
              <div className="text-sm text-gray-500">{t('unpaid')}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <AlertTriangle className="h-5 w-5 text-red-600 mb-2" />
              <div className="text-2xl font-bold text-red-600">{totalOverdue.toLocaleString()} SAR</div>
              <div className="text-sm text-gray-500">{t('overdue')}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 flex items-center justify-center">
              {pieData.length > 0 ? (
                <PieChart width={120} height={100}>
                  <Pie data={pieData} cx={55} cy={45} innerRadius={25} outerRadius={40} dataKey="value">
                    {pieData.map((_, index) => <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              ) : <div className="text-sm text-gray-400">No data</div>}
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex gap-2">
          {['all', 'paid', 'unpaid', 'overdue', 'partial'].map(s => (
            <Button key={s} variant={filterStatus === s ? 'default' : 'outline'} size="sm" onClick={() => setFilterStatus(s)}>
              {s === 'all' ? 'All' : t(s)}
            </Button>
          ))}
        </div>

        <Card>
          <CardContent className="pt-6">
            {loading ? <div className="text-center py-8">Loading...</div> : filteredPayments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No payments found.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('tenants')}</TableHead>
                    <TableHead>{t('amount')}</TableHead>
                    <TableHead>{t('dueDate')}</TableHead>
                    <TableHead>{t('paidDate')}</TableHead>
                    <TableHead>{t('status')}</TableHead>
                    <TableHead>{t('paymentMethod')}</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPayments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell className="font-medium">{getTenantName(payment.tenant_id)}</TableCell>
                      <TableCell>{payment.amount.toLocaleString()} SAR</TableCell>
                      <TableCell>{formatDate(payment.due_date)}</TableCell>
                      <TableCell>{formatDate(payment.paid_date)}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[payment.status]}`}>{t(payment.status)}</span>
                      </TableCell>
                      <TableCell>{payment.payment_method || '-'}</TableCell>
                      <TableCell>
                        {payment.status !== 'paid' && (
                          <div className="flex gap-1">
                            <Button variant="outline" size="sm" onClick={() => handleStripePay(payment)} data-testid={`stripe-pay-${payment.id}`}>
                              <CreditCard className="h-4 w-4 mr-1" /> Pay Online
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openPayDialog(payment)}>
                              <CheckCircle className="h-4 w-4 mr-1" /> Manual
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Mark as Paid Dialog */}
        <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('markAsPaid')}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t('amount')} (SAR)</Label>
                <Input type="number" value={payForm.paid_amount} onChange={(e) => setPayForm({ ...payForm, paid_amount: e.target.value })} />
              </div>
              <div>
                <Label>{t('paymentMethod')}</Label>
                <Select value={payForm.payment_method} onValueChange={(v) => setPayForm({ ...payForm, payment_method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full" onClick={handleMarkPaid}>Confirm Payment</Button>
            </div>
          </DialogContent>
        </Dialog>

        <ModuleIntelligenceBlock module="payments" testId="payments-intel" />
      </div>
    </Layout>
  );
}
